import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Play, Lock, Filter, Eye } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

const VideosPage = () => {
  const [videos, setVideos] = useState([]);
  const [featuredVideo, setFeaturedVideo] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [playingVideo, setPlayingVideo] = useState(null);
  const { user } = useAuth();

  const categories = [
    { value: '', label: 'All Videos' },
    { value: 'music_video', label: 'Music Videos' },
    { value: 'interview', label: 'Interviews' },
    { value: 'behind_the_scenes', label: 'Behind the Scenes' },
    { value: 'exclusive', label: 'Exclusive' },
    { value: 'live_session', label: 'Live Sessions' }
  ];

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const params = selectedCategory ? `?category=${selectedCategory}` : '';
        const [videosRes, featuredRes] = await Promise.all([
          axios.get(`${API}/api/videos${params}`),
          axios.get(`${API}/api/videos/featured`)
        ]);
        setVideos(videosRes.data);
        setFeaturedVideo(featuredRes.data);
      } catch (e) {
        logger.error('Failed to load videos', e);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
    // API and axios are stable constants/imports - safe to omit from dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const getCategoryLabel = (cat) => {
    const found = categories.find(c => c.value === cat);
    return found ? found.label : cat;
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    // Updated regex to handle YouTube Shorts URLs: youtube.com/shorts/VIDEO_ID
    const videoId = url.match(/(?:youtube\.com\/(?:shorts\/|(?:[^\/]+\/.+\/)|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return videoId ? `https://www.youtube.com/embed/${videoId[1]}?autoplay=1` : url;
  };


  const handleVideoClick = (video) => {
    if (!video.locked || user) {
      setPlayingVideo(video);
    }
  };

  const closeVideoModal = () => {
    setPlayingVideo(null);
  };


  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24 pb-32" data-testid="videos-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Watch & Discover</p>
        <h1 className="font-['Bebas_Neue'] text-5xl md:text-6xl">VIDEOS</h1>
        <p className="text-[#A3A3A3] mt-4 max-w-xl">
          Music videos, interviews, behind-the-scenes content, and exclusive footage.
        </p>
      </div>

      {/* Featured Video */}
      {featuredVideo && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-16" data-testid="featured-video">
          <div 
            className="relative aspect-video bg-[#1A1A1A] overflow-hidden cursor-pointer group"
            onClick={() => handleVideoClick(featuredVideo)}
          >
            {featuredVideo.youtube_url ? (
              <>
                <img
                  src={featuredVideo.thumbnail_url || `https://img.youtube.com/vi/${featuredVideo.youtube_url.match(/[?&]v=([^&]+)/)?.[1] || featuredVideo.youtube_url.split('/').pop()}/maxresdefault.jpg`}
                  alt={featuredVideo.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/50 transition-colors">
                  <div className="w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play size={36} className="text-black ml-1" />
                  </div>
                </div>
              </>
            ) : featuredVideo.thumbnail_url ? (
              <div className="relative w-full h-full">
                <img src={featuredVideo.thumbnail_url} alt={featuredVideo.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <div className="w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center cursor-pointer hover:scale-105 transition-transform">
                    <Play size={36} className="text-black ml-1" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#0A2216] to-[#0D0D0D] flex items-center justify-center">
                <Play size={64} className="text-[#D4AF37]" />
              </div>
            )}
          </div>
          <div className="mt-4">
            <span className="text-xs uppercase tracking-wider text-[#D4AF37]">Featured</span>
            <h2 className="font-['Bebas_Neue'] text-2xl md:text-3xl mt-1">{featuredVideo.title}</h2>
            <p className="text-[#A3A3A3] text-sm mt-2">{featuredVideo.description}</p>
          </div>
        </div>
      )}

      {/* Category Filter */}
      <div className="sticky top-16 z-30 bg-[#0D0D0D]/95 backdrop-blur-sm border-b border-white/10 mb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex gap-4 overflow-x-auto hide-scrollbar">
            {categories.map(cat => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`px-4 py-2 text-sm uppercase tracking-wider whitespace-nowrap transition-colors ${
                  selectedCategory === cat.value
                    ? 'bg-[#D4AF37] text-black'
                    : 'bg-[#1A1A1A] text-white hover:bg-[#262626]'
                }`}
                data-testid={`filter-${cat.value || 'all'}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Videos Grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={`skeleton-${i}`} className="space-y-3">
                <div className="aspect-video skeleton" />
                <div className="h-4 skeleton w-3/4" />
                <div className="h-3 skeleton w-1/2" />
              </div>
            ))}
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-20 bg-[#1A1A1A]">
            <p className="text-[#A3A3A3]">No videos found in this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video, idx) => (
              <div
                key={video.id}
                className="group animate-fade-in-up cursor-pointer"
                style={{ animationDelay: `${idx * 0.1}s` }}
                data-testid={`video-card-${video.id}`}
                onClick={() => handleVideoClick(video)}
              >
                <div className="relative aspect-video bg-[#1A1A1A] overflow-hidden">
                  {video.thumbnail_url ? (
                    <img
                      src={video.thumbnail_url}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#0A2216] to-[#0D0D0D]" />
                  )}
                  
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    {video.locked ? (
                      <div className="text-center">
                        <Lock size={32} className="text-[#D4AF37] mx-auto mb-2" />
                        <p className="text-sm">Purchase to unlock</p>
                      </div>
                    ) : (
                      <div className="w-14 h-14 bg-[#D4AF37] rounded-full flex items-center justify-center">
                        <Play size={24} className="text-black ml-1" />
                      </div>
                    )}
                  </div>

                  {video.is_exclusive && (
                    <span className="absolute top-2 right-2 px-2 py-1 bg-[#D4AF37] text-black text-xs uppercase tracking-wider">
                      Exclusive
                    </span>
                  )}

                  {video.duration > 0 && (
                    <span className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 text-xs tabular-nums">
                      {formatDuration(video.duration)}
                    </span>
                  )}
                </div>

                <div className="mt-3">
                  <span className="text-xs uppercase tracking-wider text-[#D4AF37]">
                    {getCategoryLabel(video.category)}
                  </span>
                  <h3 className="font-medium mt-1 group-hover:text-[#D4AF37] transition-colors">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-3 mt-2 text-xs text-[#737373]">
                    <span className="flex items-center gap-1">
                      <Eye size={12} />
                      {video.view_count?.toLocaleString() || 0} views
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Video Player Modal */}
      {playingVideo && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={closeVideoModal}
        >
          <div 
            className="relative w-full max-w-6xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={closeVideoModal}
              className="absolute -top-14 right-0 text-white hover:text-[#D4AF37] transition-colors z-10"
              aria-label="Close video"
            >
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Video container with 16:9 aspect ratio */}
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              {playingVideo.youtube_url ? (
                <iframe
                  src={getYouTubeEmbedUrl(playingVideo.youtube_url)}
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  title={playingVideo.title}
                />
              ) : playingVideo.video_url ? (
                <video
                  src={playingVideo.video_url}
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  controls
                  autoPlay
                />
              ) : (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center text-white bg-[#1A1A1A] rounded-lg">
                  <p>Video not available</p>
                </div>
              )}
            </div>

            {/* Video title */}
            <div className="mt-4 text-white">
              <h3 className="font-['Bebas_Neue'] text-2xl">{playingVideo.title}</h3>
              {playingVideo.description && (
                <p className="text-sm text-[#A3A3A3] mt-2">{playingVideo.description}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideosPage;
